import React, { Component, ErrorInfo, ReactNode } from 'react';
import { handleApiError, AppError, ErrorType, ErrorSeverity } from '../../utils/errorHandling';
import notificationService from '../../services/notificationService';

// ============================================================================
// ERROR BOUNDARY COMPONENT
// ============================================================================
// Catches JavaScript errors anywhere in child component tree and displays
// a fallback UI instead of crashing the entire application.

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Custom fallback UI to show when error occurs */
  fallback?: ReactNode;
  /** Custom fallback render function with error details */
  fallbackRender?: (props: FallbackProps) => ReactNode;
  /** Callback when error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Whether to show notification on error */
  showNotification?: boolean;
  /** Whether to log error to console */
  logError?: boolean;
  /** Custom error message to display */
  errorMessage?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: AppError | null;
  errorInfo: ErrorInfo | null;
}

export interface FallbackProps {
  error: AppError | null;
  errorInfo: ErrorInfo | null;
  resetError: () => void;
}

/**
 * Default fallback UI component
 */
const DefaultFallbackUI: React.FC<FallbackProps> = ({ error, resetError }) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '200px',
        padding: '40px',
        textAlign: 'center',
        backgroundColor: '#fef2f2',
        borderRadius: '8px',
        border: '1px solid #fecaca',
        margin: '20px',
      }}
    >
      <div
        style={{
          fontSize: '48px',
          marginBottom: '16px',
        }}
      >
        ⚠️
      </div>
      <h2
        style={{
          color: '#991b1b',
          fontSize: '20px',
          fontWeight: 600,
          marginBottom: '8px',
        }}
      >
        Something went wrong
      </h2>
      <p
        style={{
          color: '#b91c1c',
          fontSize: '14px',
          marginBottom: '24px',
          maxWidth: '400px',
        }}
      >
        {error?.userMessage || 'An unexpected error occurred. Please try again.'}
      </p>
      <div style={{ display: 'flex', gap: '12px' }}>
        <button
          onClick={resetError}
          style={{
            padding: '10px 20px',
            backgroundColor: '#dc2626',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 500,
            transition: 'background-color 0.2s',
          }}
          onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#b91c1c')}
          onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#dc2626')}
        >
          Try Again
        </button>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '10px 20px',
            backgroundColor: 'white',
            color: '#dc2626',
            border: '1px solid #dc2626',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 500,
            transition: 'background-color 0.2s',
          }}
          onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#fef2f2')}
          onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'white')}
        >
          Reload Page
        </button>
      </div>
      {process.env.NODE_ENV === 'development' && error && (
        <details
          style={{
            marginTop: '24px',
            padding: '16px',
            backgroundColor: '#fee2e2',
            borderRadius: '6px',
            textAlign: 'left',
            maxWidth: '600px',
            width: '100%',
          }}
        >
          <summary
            style={{
              cursor: 'pointer',
              fontWeight: 500,
              color: '#991b1b',
            }}
          >
            Error Details (Development Only)
          </summary>
          <pre
            style={{
              marginTop: '12px',
              padding: '12px',
              backgroundColor: '#1f2937',
              color: '#f3f4f6',
              borderRadius: '4px',
              fontSize: '12px',
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {JSON.stringify(
              {
                type: error.type,
                message: error.message,
                severity: error.severity,
                timestamp: error.timestamp,
                details: error.details,
              },
              null,
              2
            )}
          </pre>
        </details>
      )}
    </div>
  );
};

/**
 * Error Boundary Component
 *
 * Catches JavaScript errors in child component tree and displays a fallback UI.
 *
 * @example
 * // Basic usage
 * <ErrorBoundary>
 *   <MyComponent />
 * </ErrorBoundary>
 *
 * @example
 * // With custom fallback
 * <ErrorBoundary fallback={<CustomErrorPage />}>
 *   <MyComponent />
 * </ErrorBoundary>
 *
 * @example
 * // With fallback render function
 * <ErrorBoundary
 *   fallbackRender={({ error, resetError }) => (
 *     <div>
 *       <p>Error: {error?.message}</p>
 *       <button onClick={resetError}>Reset</button>
 *     </div>
 *   )}
 * >
 *   <MyComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  static defaultProps: Partial<ErrorBoundaryProps> = {
    showNotification: true,
    logError: true,
  };

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Convert error to AppError format
    const appError = handleApiError(error, {
      showNotification: false,
      logError: false,
    });

    return {
      hasError: true,
      error: appError,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const { onError, showNotification, logError, errorMessage } = this.props;

    // Update state with error info
    this.setState({ errorInfo });

    // Log error if enabled
    if (logError) {
      console.error('[ErrorBoundary] Caught error:', error);
      console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);
    }

    // Show notification if enabled
    if (showNotification) {
      notificationService.error(
        'Application Error',
        errorMessage || 'Something went wrong. Please try again or reload the page.'
      );
    }

    // Call custom error handler if provided
    if (onError) {
      onError(error, errorInfo);
    }
  }

  /**
   * Reset error state to allow re-rendering children
   */
  resetError = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback, fallbackRender } = this.props;

    if (hasError) {
      // Use custom fallback render function if provided
      if (fallbackRender) {
        return fallbackRender({
          error,
          errorInfo,
          resetError: this.resetError,
        });
      }

      // Use custom fallback component if provided
      if (fallback) {
        return fallback;
      }

      // Use default fallback UI
      return (
        <DefaultFallbackUI
          error={error}
          errorInfo={errorInfo}
          resetError={this.resetError}
        />
      );
    }

    return children;
  }
}

/**
 * Higher-order component to wrap a component with ErrorBoundary
 */
export function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>
): React.FC<P> {
  const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

  const ComponentWithErrorBoundary: React.FC<P> = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );

  ComponentWithErrorBoundary.displayName = `withErrorBoundary(${displayName})`;

  return ComponentWithErrorBoundary;
}

export default ErrorBoundary;
